import {
  Box,
  Center,
  HStack,
  Image,
  Skeleton,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { Attachment, Transaction } from "../../../API";
import { fetchReceipt } from "../../../utils";
import ReceiptLightbox from "./receipt-lightbox";

// Only the fields ReceiptPreview + ReceiptLightbox actually consume.
// Allows passing TransactionFull or synthetic objects without `as any`.
type ReceiptPreviewProps = {
  attachments: Pick<Attachment, "id" | "key">[];
  transaction: Pick<
    Transaction,
    "id" | "title" | "paidAt" | "currencyCode" | "amount"
  >;
};

const isPdf = (key: string) => key.toLowerCase().endsWith(".pdf");

const ReceiptPreview = ({ attachments, transaction }: ReceiptPreviewProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loadState, setLoadState] = useState<"loading" | "loaded" | "error">(
    "loading"
  );
  const { isOpen, onOpen, onClose } = useDisclosure();
  const prevAttachmentsRef = useRef(attachments);

  // Reset activeIndex when attachments reference changes (transaction switch)
  useEffect(() => {
    if (prevAttachmentsRef.current !== attachments) {
      setActiveIndex(0);
      prevAttachmentsRef.current = attachments;
    }
  }, [attachments]);

  const activeAttachment = attachments[activeIndex];

  // Load preview for active attachment via S3 pre-signed URL
  useEffect(() => {
    if (!activeAttachment) return;

    setBlobUrl(null);
    setLoadState("loading");

    let createdUrl: string | null = null;
    let cancelled = false;

    fetchReceipt(activeAttachment.key).then((data) => {
      if (cancelled) return;
      if (data) {
        createdUrl = URL.createObjectURL(data.blob);
        setBlobUrl(createdUrl);
        setLoadState("loaded");
      } else {
        setLoadState("error");
      }
    });

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [activeAttachment?.key]);

  if (attachments.length === 0) {
    return (
      <Text fontSize="sm" color="gray.400">
        Kein Beleg vorhanden
      </Text>
    );
  }

  const activeIsPdf = activeAttachment ? isPdf(activeAttachment.key) : false;

  return (
    <VStack width="full" spacing="2">
      {/* Active preview area */}
      <Box
        width="full"
        bg="#F7F7F8"
        borderRadius="md"
        overflow="hidden"
        cursor="pointer"
        onClick={onOpen}
        position="relative"
      >
        {loadState === "loading" && <Skeleton height="500px" width="full" />}
        {loadState === "error" && (
          <Center height="200px">
            <Text fontSize="sm" color="gray.400">
              Beleg konnte nicht geladen werden
            </Text>
          </Center>
        )}
        {loadState === "loaded" && blobUrl && (
          <>
            {activeIsPdf ? (
              <Box position="relative">
                <Box
                  as="iframe"
                  src={blobUrl}
                  width="full"
                  height="500px"
                  border="none"
                  title="Beleg PDF"
                  pointerEvents="none"
                />
                {/* Transparent overlay to capture clicks (iframe swallows them otherwise) */}
                <Box position="absolute" inset="0" cursor="pointer" />
              </Box>
            ) : (
              <Image
                src={blobUrl}
                objectFit="contain"
                maxH="500px"
                width="full"
                alt="Beleg"
              />
            )}
          </>
        )}
      </Box>

      {/* Thumbnail strip -- only shown when >1 attachment */}
      {attachments.length > 1 && (
        <HStack spacing="2" width="full" overflowX="auto">
          {attachments.map((attachment, index) => (
            <Box
              key={attachment.id}
              boxSize="48px"
              bg="#F7F7F8"
              borderRadius="md"
              border="2px"
              borderColor={
                index === activeIndex ? "primary.500" : "transparent"
              }
              cursor="pointer"
              flexShrink={0}
              onClick={() => setActiveIndex(index)}
              overflow="hidden"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              {isPdf(attachment.key) ? (
                <Text fontSize="xs" fontWeight="semibold" color="gray.500">
                  PDF
                </Text>
              ) : (
                <Text fontSize="xs" color="gray.400">
                  Bild
                </Text>
              )}
            </Box>
          ))}
        </HStack>
      )}

      {/* Lightbox modal */}
      <ReceiptLightbox
        attachments={attachments}
        initialIndex={activeIndex}
        transaction={transaction}
        isOpen={isOpen}
        onClose={onClose}
      />
    </VStack>
  );
};

export default ReceiptPreview;
